import React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * Search + filters + export, the row that sits under the filter tabs on every
 * list screen in the reference. Search takes the width because it is what
 * vendors actually reach for; the rest are fixed-width trailing controls.
 */

export interface ToolbarProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  /** Trailing controls — a filter dropdown, a sort, an export button. */
  children?: React.ReactNode;
  className?: string;
}

const Toolbar: React.FC<ToolbarProps> = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  children,
  className,
}) => (
  <div className={cn('flex flex-wrap items-center gap-2', className)}>
    {onSearchChange ? (
      <div className="relative flex-1 min-w-[200px]">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-flup-ink-3 pointer-events-none"
          aria-hidden
        />
        <input
          type="search"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          className={cn(
            'w-full h-10 pl-9 pr-9 rounded-flup-sm bg-flup-surface border border-flup-hairline',
            'font-body text-sm text-flup-ink placeholder:text-flup-ink-3',
            'transition-colors focus:outline-none focus:border-flup-brand focus:ring-1 focus:ring-flup-brand',
            // The browser's own clear affordance sits badly next to ours.
            '[&::-webkit-search-cancel-button]:appearance-none',
          )}
        />
        {searchValue ? (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            aria-label="Clear search"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 grid place-items-center w-6 h-6 rounded-flup-xs text-flup-ink-3 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
          >
            <X className="w-3.5 h-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
    ) : null}

    {children}
  </div>
);

/**
 * A labelled trailing control that matches the search field's height and skin —
 * use it to wrap a native select or a popover trigger so the row stays even.
 */
export const ToolbarControl: React.FC<{
  icon?: React.ReactNode;
  label?: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  className?: string;
  children?: React.ReactNode;
}> = ({ icon, label, onClick, active, className, children }) => (
  <div
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-2 h-10 px-3 rounded-flup-sm bg-flup-surface border transition-colors',
      onClick && 'cursor-pointer hover:bg-flup-surface-alt',
      active ? 'border-flup-green' : 'border-flup-hairline',
      className,
    )}
  >
    {icon ? <span className="text-flup-ink-3 shrink-0">{icon}</span> : null}
    {label ? (
      <Text variant="labelSemi" tone="secondary" as="span">
        {label}
      </Text>
    ) : null}
    {children}
  </div>
);

export default React.memo(Toolbar);
